import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api/client';
import { deleteData, postData, postFormData, putData } from '@/lib/api/client-v2';
import { pushGoogleEvent } from '@/lib/analytics';
import { isEmpty } from '@/lib/collections';
import { DATE_ISO, formatDate, isValidDate, type DateInput } from '@/lib/dates';
import { notify, type NotifyOptions } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import type { Contract } from '@/types/api/contract';
import { ENDPOINTS } from '@/types/api/endpoints';

/*
 * Écritures de la fiche contrat : loi Hamon, fin d'assurance transitoire, garantie mobilité et
 * tiers payeur. Reprises des anciens middlewares `contracts`, `garantie.mobilite` et `tiers_payeur`.
 */

const errorPopin = (html: string) =>
    notify({
        html,
        icon: 'error',
        title: 'Attention !',
        confirmButtonText: "J'ai compris",
    });

/* ---------- Loi Hamon ---------- */

export type ConfirmResilHamon = 'T' | 'F' | 'help';

/** Réponse de l'assuré sur la résiliation de son ancien contrat (`POST /contracts/{id}/hamon/resiliation`). */
export const useConfirmResilHamon = () =>
    useMutation({
        mutationFn: ({ contractId, confirmResil }: { contractId: string | number; confirmResil: ConfirmResilHamon }) =>
            postData<null>(ENDPOINTS.contractHamon(contractId), { confirm_resil: confirmResil }),
        onSuccess: (_data, { confirmResil }) => {
            const notificationMessage =
                confirmResil === 'T'
                    ? 'Vous nous avez confirmé avoir recu la notificiation de la résiliation de votre ancien contrat'
                    : confirmResil === 'F'
                      ? "Vérifiez auprès de votre ancienne assurance que votre contrat est bien résilié ? Partagez nous l'info ici dès que vous l'obtenez !"
                      : 'Merci pour votre retour, un conseiller vous recontacte dans les meilleurs délais';

            void notify({
                html: `<div><p>${notificationMessage}</p></div>`,
                type: 'success',
                title: 'Loi Hamon',
                confirmButtonText: "J'ai compris",
            } as NotifyOptions);
        },
        onError: (error) => {
            void errorPopin(`<p>${getApiErrorMessage(error)}</p>`);
        },
    });

/* ---------- Assurance transitoire ---------- */

/** Fin de l'assurance transitoire de l'ancien véhicule (`POST /contracts/{id}/transitoire/terminate`). */
export const useTerminateAssuranceTransitoire = () =>
    useMutation({
        mutationFn: ({ contractId }: { contractId: string | number }) =>
            postData<null>(ENDPOINTS.contractTransitoireTerminate(contractId)),
        onSuccess: () => {
            void notify({
                html: `<div><p>Votre ancien véhicule n'est plus couvert par l'assurance transitoire.</p></div>`,
                type: 'success',
                title: "Fin de l'assurance transitoire",
                confirmButtonText: "J'ai compris",
            } as NotifyOptions);
        },
        onError: (error) => {
            void errorPopin(`<p>${getApiErrorMessage(error)}</p>`);
        },
    });

/* ---------- Garantie mobilité ---------- */

export interface GarantieMobiliteForm {
    circonstances: string;
    /** Pièces indexées par code de document ; seule `X` (justificatif) est attendue. */
    files: Record<string, File | undefined>;
}

/**
 * Déclaration de garantie mobilité (`POST /contracts/{id}/mobilite-claim`), en multipart :
 * `circonstances` + `file`. L'éligibilité se lit sur `actions.garantie_mobilite` de la fiche.
 */
export const usePostGarantieMobilite = () =>
    useMutation({
        mutationFn: ({ contractId, form }: { contractId: string | number; form: GarantieMobiliteForm; successFunction?: () => void }) => {
            pushGoogleEvent({ event: 'click', actionGoogle: 'clickButton', libelle: 'Garantie mobilité' });

            const data = new FormData();

            data.append('circonstances', form.circonstances);

            /* files est un objet indexé par code de document ; la garantie mobilité n'en attend
               qu'un seul, le justificatif X, que la v2 reçoit sous `file` (elle le range en X). */
            if (form.files?.X) {
                data.append('file', form.files.X);
            }

            return postFormData(ENDPOINTS.contractMobiliteClaim(contractId), data);
        },
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<p>Votre demande a bien été enregistrée, vous allez recevoir un email vous confirmant son enregistrement.</p>',
                icon: 'success',
                title: 'Merci !',
                confirmButtonText: "J'ai compris",
                successFunction,
            });
        },
        onError: (error) => {
            void errorPopin(`<p>Une erreur est survenue lors de votre demande. ${getApiErrorMessage(error)}</p>`);
        },
    });

/* ---------- Tiers payeur ---------- */

export interface TiersPayeurForm {
    type?: string;
    firstname?: string;
    lastname?: string;
    birthname?: string;
    birthdate?: DateInput;
    birthcountry?: string;
    birthcity?: string;
    relationship?: string;
    third_payeur_reason?: string;
}

/** Corps v2 du tiers payeur (`ThirdPartyPayerRequest`). */
export interface ThirdPartyPayerRequest {
    type?: string;
    last_name?: string;
    first_name?: string;
    birth_name?: string;
    birth_date?: string;
    birth_country?: string;
    birth_city?: string;
    relationship?: string;
    reason_other?: string;
}

/** Noms du formulaire (ceux de la v1) → noms de la v2. */
const toThirdPartyPayerRequest = (form: TiersPayeurForm): ThirdPartyPayerRequest => ({
    type: form.type,
    last_name: form.lastname,
    first_name: form.firstname,
    birth_name: form.birthname,
    birth_date:
        form.type === 'P' && form.birthdate && isValidDate(form.birthdate)
            ? formatDate(form.birthdate, DATE_ISO)
            : (form.birthdate as string | undefined),
    birth_country: form.birthcountry,
    birth_city: form.birthcity,
    relationship: form.relationship,
    reason_other: form.third_payeur_reason,
});

/**
 * Ajout / modification du tiers payeur (`PUT /contracts/{id}/tiers-payeur`).
 *
 * La date de naissance d'une personne physique part en `yyyy-MM-dd` ; les valeurs vides
 * (au sens de `isEmpty`, nombres et booléens compris) ne sont pas envoyées.
 */
export const useUpdateTiersPayeur = () =>
    useMutation({
        mutationFn: ({ contractId, form }: { contractId: string | number; form: TiersPayeurForm; successFunction?: () => void }) =>
            putData<null>(
                ENDPOINTS.contractTiersPayeur(contractId),
                Object.fromEntries(Object.entries(toThirdPartyPayerRequest(form)).filter(([, value]) => !isEmpty(value))),
            ),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<div><p>Votre demande de modification a bien été prise en compte</p></div>',
                icon: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                successFunction,
            });
        },
        onError: (error) => {
            void errorPopin('<p>Une erreur est survenue : ' + getApiErrorMessage(error) + '</p>');
        },
    });

/**
 * Suppression du tiers payeur (`DELETE /contracts/{id}/tiers-payeur`).
 *
 * Au succès, le contrat en cache perd son tiers payeur (ancien `SET_CONTRACT_THIRD_PARTY_PAYER`).
 */
export const useDeleteTiersPayeur = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ contractId }: { contractId: string | number }) =>
            deleteData<null>(ENDPOINTS.contractTiersPayeur(contractId)),
        onSuccess: (_data, { contractId }) => {
            queryClient.setQueryData<Contract>(queryKeys.contract(contractId), (contract) =>
                contract ? { ...contract, tiers_payeur: [] } : contract,
            );

            void notify({
                html: '<div><p>Votre demande de suppression a bien été prise en compte</p></div>',
                icon: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
            });
        },
        onError: (error) => {
            void errorPopin('<p>Une erreur est survenue : ' + getApiErrorMessage(error) + '</p>');
        },
    });
};
